import { useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FirmwareTrack, MaintenanceType, UserRole } from '@bhops/shared';
import { fleetApi } from '@/api/fleet';
import { dashboardApi } from '@/api/dashboard';
import { ecnsApi } from '@/api/ecns';
import { solutionsApi } from '@/api/solutions';
import { serviceOrdersApi } from '@/api/service-orders';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/components/Toast';
import { robotStatusLabel } from '@/lib/enum-labels';
// 추가수정 2차(26-07-27) §2 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
// v0.4.0 §3.6 — 부품/솔루션/결함 LOT 활성 데이터.
import type {
  EcnRobotDto,
  EcnSummaryDto,
  PartCategory,
  SolutionCategory,
  SolutionStatus,
  SolutionStoreUsageDto,
  CustomerSolutionRollupDto,
} from '@bhops/shared';

// Plan v0.3.0 §J-3 (rev 3) — Fleet Tracker MVP page.
//
// The page is composed of:
//   1. 4 risk indicator cards (3 active + 1 v0.4.0 placeholder)
//   2. 3 sub-view tabs (펌웨어 active; 부품 / 솔루션 placeholder)
//   3. Active sub-view: firmware version distribution + stale-firmware
//      robot list with click-through to RobotDetailPage
//
// Permission (§J-7): VIEWER sees the page silhouette but the data
// queries are gated server-side (ForbiddenException). We render a
// soft "manager required" message when role check fails so the
// sidebar entry can still be visible (Field group housekeeping).

const TRACK_LABEL: Record<FirmwareTrack, string> = {
  BINARY: '바이너리',
  BUNDLE: '번들',
  TABLET_APP: '태블릿 앱',
};

type SubView = 'firmware' | 'parts' | 'solution';

export function FleetTrackerPage() {
  const role = useAuthStore((s) => s.user?.role);
  // §J-7: Manager+ only. VIEWER + non-manager users see a friendly
  // notice instead of empty cards. Sales / field viewers visiting
  // the link from the sidebar still get a clear "운영 권한 필요"
  // message rather than a stack trace.
  const canAccess =
    role === UserRole.ADMIN ||
    role === UserRole.MANAGER ||
    role === UserRole.SALES_MANAGER ||
    role === UserRole.MFG_MANAGER ||
    role === UserRole.FIELD_MANAGER;

  const [subView, setSubView] = useState<SubView>('firmware');

  // Hooks must run unconditionally — `enabled` gates the network
  // call when the role check fails. The early-return below renders
  // the access notice without ever rendering the data sections.
  const riskQ = useQuery({
    queryKey: ['fleet', 'risk-summary'],
    queryFn: () => fleetApi.riskSummary(),
    staleTime: 60_000,
    enabled: canAccess,
  });

  // v0.4.0 §3.6 — 결함 의심 LOT 카운트 (4번째 risk card 데이터).
  const defectLotsQ = useQuery({
    queryKey: ['fleet', 'defect-lots'],
    queryFn: () => dashboardApi.fleetDefectLots(),
    staleTime: 60_000,
    enabled: canAccess,
  });

  if (!canAccess) {
    return (
      <div style={{ padding: 32, maxWidth: 640 }}>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          추적 · 분석
        </h1>
        <p style={{ color: 'var(--fg-muted)', marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
          이 페이지는 관리자와 매니저만 볼 수 있습니다. 영업이나 필드 뷰어는 로봇 상세 화면에서
          펌웨어와 배치 정보를 확인해 주세요.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          추적 · 분석
        </h1>
      </header>

      {/* §J-3 위험 지표 4 카드 (3 활성 + 1 v0.4.0 placeholder) */}
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <RiskCard
          label="총 관리 로봇"
          value={riskQ.data?.totalManaged ?? null}
          loading={riskQ.isLoading}
        />
        <RiskCard
          label="구버전 FW 로봇"
          value={riskQ.data?.oldFirmwareCount ?? null}
          loading={riskQ.isLoading}
          tone={riskQ.data && riskQ.data.oldFirmwareCount > 0 ? 'warning' : 'default'}
          hint="90일 넘게 업데이트하지 않았거나 기록이 없는 로봇"
        />
        <RiskCard
          label="미할당 매장"
          value={riskQ.data?.unassignedStores ?? null}
          loading={riskQ.isLoading}
          hint="활성 배치 없음"
        />
        {/* v0.4.0 §3.6 — 결함 의심 LOT 활성화. */}
        <RiskCard
          label="결함 의심 LOT"
          value={
            defectLotsQ.data
              ? defectLotsQ.data.suspectedCount + defectLotsQ.data.confirmedCount
              : null
          }
          loading={defectLotsQ.isLoading}
          tone={
            defectLotsQ.data && defectLotsQ.data.confirmedCount > 0
              ? 'danger'
              : defectLotsQ.data && defectLotsQ.data.suspectedCount > 0
                ? 'warning'
                : 'default'
          }
          hint={
            defectLotsQ.data
              ? `의심 ${defectLotsQ.data.suspectedCount}, 확정 ${defectLotsQ.data.confirmedCount}`
              : undefined
          }
        />
      </section>

      {/* §J-3 sub-view 탭 — v0.4.0 부터 부품/솔루션 활성화. */}
      <section style={card}>
        <div role="tablist" aria-label="추적 · 분석 보기" style={tabStrip}>
          <TabButton active={subView === 'firmware'} onClick={() => setSubView('firmware')}>
            펌웨어 추적
          </TabButton>
          <TabButton active={subView === 'parts'} onClick={() => setSubView('parts')}>
            부품 추적
          </TabButton>
          <TabButton active={subView === 'solution'} onClick={() => setSubView('solution')}>
            솔루션 추적
          </TabButton>
        </div>

        <div style={{ padding: 16 }}>
          {subView === 'firmware' && <FirmwareSubView />}
          {subView === 'parts' && <PartsSubView />}
          {subView === 'solution' && <SolutionSubView />}
        </div>
      </section>
    </div>
  );
}

// ============================================================
// Firmware sub-view (active in v0.3.0)
// ============================================================

function FirmwareSubView() {
  const distQ = useQuery({
    queryKey: ['fleet', 'firmware-distribution'],
    queryFn: () => fleetApi.firmwareDistribution(),
    staleTime: 60_000,
  });

  const staleQ = useQuery({
    queryKey: ['fleet', 'stale-firmware-robots'],
    queryFn: () => fleetApi.staleFirmwareRobots(),
    staleTime: 60_000,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Distribution */}
      <div>
        <h3 className="kuun-h4" style={{ margin: 0 }}>
          버전 분포
        </h3>
        <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
          트랙별로 쓰고 있는 버전입니다. 행을 누르면 로봇 상세에서 그 펌웨어 기록을 볼 수 있습니다.
        </div>
        {distQ.error && <div style={errorBox}>{extractApiError(distQ.error)}</div>}
        <div style={{ marginTop: 8, display: 'grid', gap: 12 }}>
          {(distQ.data ?? []).map((d) => (
            <DistributionTrack key={d.track} dist={d} />
          ))}
        </div>
      </div>

      {/* Stale-firmware robot list (drill-down) */}
      <div>
        <h3 className="kuun-h4" style={{ margin: 0 }}>
          90일 이상 미업데이트 로봇
        </h3>
        <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
          바이너리 업데이트일 기준입니다. 행을 누르면 로봇 상세로 이동합니다.
        </div>
        {staleQ.error && <div style={errorBox}>{extractApiError(staleQ.error)}</div>}
        <table style={tableBase}>
          <thead>
            <tr>
              <th style={th}>SN</th>
              <th style={th}>모델</th>
              <th style={th}>매장</th>
              <th style={{ ...th, textAlign: 'center' }}>상태</th>
              <th style={{ ...th, textAlign: 'right' }}>마지막 업데이트</th>
            </tr>
          </thead>
          <tbody>
            {(staleQ.data ?? []).map((r) => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td style={tdMono}>
                  <Link to={`/robots/${r.id}`} style={linkStyle}>
                    {r.serialShort ? `#${r.serialShort}` : (r.serialLong ?? r.id.slice(0, 8))}
                  </Link>
                </td>
                <td style={td}>{r.model.displayName}</td>
                <td style={td}>
                  {r.deployments[0] ? (
                    [r.deployments[0].store.name, r.deployments[0].store.branchName]
                      .filter(Boolean)
                      .join(' · ')
                  ) : (
                    <span style={{ color: 'var(--fg-muted)' }}>—</span>
                  )}
                </td>
                <td style={tdCenter}>{robotStatusLabel(r.status)}</td>
                <td style={{ ...tdMono, textAlign: 'right', color: 'var(--fg-muted)' }}>
                  {kstYmd(r.binaryUpdatedAt) ?? '미기록'}
                </td>
              </tr>
            ))}
            {!staleQ.isLoading && (staleQ.data ?? []).length === 0 && (
              <tr>
                <td colSpan={5} style={empty}>
                  최근 90일 내 모든 로봇이 업데이트되었습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DistributionTrack({ dist }: { dist: import('@/api/fleet').FirmwareTrackDistribution }) {
  const total = dist.versions.reduce((s, v) => s + v.count, 0) + dist.unknownCount;
  return (
    <div style={trackBlock}>
      <div style={trackHeader}>
        <strong style={{ fontSize: 13 }}>{TRACK_LABEL[dist.track]}</strong>
        <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
          {dist.versions.length}개 버전, 총 {total}대
        </span>
      </div>
      {dist.versions.length === 0 && dist.unknownCount === 0 && (
        <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
          이 트랙은 아직 이벤트가 없습니다.
        </div>
      )}
      {dist.versions.map((v) => (
        <div key={v.version} style={versionRow}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, minWidth: 100 }}>
            {v.version}
          </span>
          <div
            style={{
              flex: 1,
              position: 'relative',
              height: 8,
              background: 'var(--bg-sunken)',
              borderRadius: 4,
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                width: `${total > 0 ? (v.count / total) * 100 : 0}%`,
                background: 'var(--blue-500)',
                borderRadius: 4,
              }}
            />
          </div>
          <span style={{ fontSize: 12, minWidth: 40, textAlign: 'right' }}>{v.count}</span>
          <span
            style={{ fontSize: 11, color: 'var(--fg-muted)', minWidth: 80, textAlign: 'right' }}
          >
            {kstYmd(v.latestAppliedAt) ?? '-'}
          </span>
        </div>
      ))}
      {dist.unknownCount > 0 && (
        <div style={{ ...versionRow, color: 'var(--fg-muted)' }}>
          <span style={{ fontSize: 12, minWidth: 100, fontStyle: 'italic' }}>기록 없음</span>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 12, minWidth: 40, textAlign: 'right' }}>
            {dist.unknownCount}
          </span>
          <span style={{ minWidth: 80 }} />
        </div>
      )}
    </div>
  );
}

// ============================================================
// §3.6 (S3 Phase 3.5) — 부품 추적 sub-view (ECN 역추적 → 일괄 ServiceOrder)
// ============================================================
//
// 흐름: 변경점(ECN) 검색 → 선택 → 영향 받는 (현장에 나간) 로봇 + 고객사/매장 표시
// → 체크박스 선택 → [선택 로봇 ServiceOrder 생성] 일괄(PART_REPLACE, 사유에
// ECN 코드 자동) + 행 단위 [ServiceOrder] + [CSV]. PartLot recall(부품 LOT)은
// 기존 /parts/lots 페이지로 링크. 하단에 부품 요약(보증/결함 LOT) 컨텍스트 유지.

const PART_CATEGORY_LABEL: Record<PartCategory, string> = {
  GRILLER: '그릴러',
  LOADER: '로더',
  GAS_VALVE: '가스 밸브',
  FIRMWARE_BOARD: '펌웨어 보드',
  SENSOR_SUITE: '센서 모듈',
  POWER_UNIT: '전원 유닛',
  CONSUMABLE: '소모품',
  ACCESSORY: '액세서리',
};

function PartsSubView() {
  const toast = useToast();
  const queryClient = useQueryClient();

  // ECN 검색 상태.
  const [search, setSearch] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [selectedEcn, setSelectedEcn] = useState<EcnSummaryDto | null>(null);
  // 현장에 나간 로봇만(운영중·기타) — 현장 recall 기본 ON. 2026-09-23 기타 포함.
  const [operatingOnly, setOperatingOnly] = useState(true);
  // 일괄 생성 선택 체크박스.
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const listQ = useQuery({
    queryKey: ['fleet', 'ecn-search', submittedQuery],
    queryFn: () => ecnsApi.list({ q: submittedQuery || undefined }),
    enabled: submittedQuery.length > 0,
    staleTime: 30_000,
  });

  const robotsQ = useQuery({
    queryKey: ['fleet', 'ecn-robots', selectedEcn?.id, operatingOnly],
    queryFn: () => ecnsApi.listRobots(selectedEcn!.id, { operatingOnly }),
    enabled: !!selectedEcn,
    staleTime: 30_000,
  });

  const robots = useMemo(() => robotsQ.data?.items ?? [], [robotsQ.data]);

  // 일괄 / 단건 ServiceOrder 생성 (둘 다 batch-recall 사용 — 단건은 1개 배열).
  const recall = useMutation({
    mutationFn: (robotIds: string[]) =>
      serviceOrdersApi.batchRecall({
        robotIds,
        maintenanceType: MaintenanceType.PART_REPLACE,
        ecnCodeRef: selectedEcn?.code,
      }),
    onSuccess: (res) => {
      if (res.createdCount > 0) {
        toast.success(
          `서비스 오더 ${res.createdCount}건을 만들었습니다.${
            res.failedCount > 0 ? ` ${res.failedCount}건은 건너뛰었습니다.` : ''
          }`,
          '부품 추적 리콜',
        );
      } else {
        toast.warn('만들어진 서비스 오더가 없습니다. 현장에 나간 로봇을 골랐는지 확인해 주세요.');
      }
      // 첫 실패 사유를 안내.
      const firstError = res.results.find((r) => r.error);
      if (firstError?.error) toast.info(firstError.error);
      setChecked(new Set());
      void queryClient.invalidateQueries({ queryKey: ['service-orders'] });
    },
    onError: (err) => toast.error(extractApiError(err), '서비스 오더 생성 실패'),
  });

  function pickEcn(ecn: EcnSummaryDto): void {
    setSelectedEcn(ecn);
    setChecked(new Set());
  }

  function toggle(robotId: string): void {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(robotId)) next.delete(robotId);
      else next.add(robotId);
      return next;
    });
  }

  function toggleAll(): void {
    setChecked((prev) =>
      prev.size === robots.length ? new Set() : new Set(robots.map((r) => r.robotId)),
    );
  }

  function exportCsv(): void {
    if (!selectedEcn) return;
    const header = [
      'serialShort',
      'serialLong',
      'modelCode',
      'status',
      '고객사',
      '매장',
      'appliedAt',
    ];
    const lines = robots.map((r) =>
      [
        r.serialShort ?? '',
        r.serialLong ?? '',
        r.modelCode,
        r.status ?? '',
        r.customerDisplayName ?? '',
        r.storeName ?? '',
        // 추가수정 2차(26-07-27) §2 — CSV 도 KST 기준 날짜. 단 표 계산에
        // 쓰이는 파일이라 구분자는 하이픈(ISO)으로 되돌린다.
        kstYmd(r.appliedAt)?.replace(/\./g, '-') ?? '',
      ]
        .map(csvCell)
        .join(','),
    );
    const csv = [header.join(','), ...lines].join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ecn-${selectedEcn.code}-recall.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const ecnItems = listQ.data?.items ?? [];
  const busy = recall.isPending;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h3 className="kuun-h4" style={{ margin: 0 }}>
          변경점(ECN)으로 영향받은 로봇 찾기
        </h3>
        <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
          변경점(ECN)을 검색하면 그 변경이 적용된 로봇과 고객사, 매장을 찾습니다. 고른 로봇에 부품
          교체 서비스 오더를 한 번에 만들 수 있고, 사유에는 ECN 코드가 자동으로 들어갑니다. LOT 단위
          리콜은 로봇 상세의 '부품' 탭에서 처리합니다.
        </div>
      </div>

      {/* ECN 검색 바. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmittedQuery(search.trim());
        }}
        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <input
          type="text"
          aria-label="변경점(ECN) 검색"
          placeholder="코드나 설명으로 검색. 예: N25052301, 불감지센서"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 240,
            height: 34,
            padding: '0 10px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-input)',
            fontSize: 13,
          }}
        />
        <button type="submit" style={primaryBtn}>
          검색
        </button>
      </form>

      {/* ECN 검색 결과 목록. */}
      {submittedQuery.length > 0 && (
        <div>
          {listQ.isLoading ? (
            <div style={empty}>불러오는 중…</div>
          ) : listQ.error ? (
            <div style={errorBox}>{extractApiError(listQ.error)}</div>
          ) : ecnItems.length === 0 ? (
            <div style={emptyBlock}>검색 결과가 없습니다.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ecnItems.map((ecn) => (
                <button
                  key={ecn.id}
                  type="button"
                  onClick={() => pickEcn(ecn)}
                  style={{
                    ...ecnRow,
                    borderColor:
                      selectedEcn?.id === ecn.id ? 'var(--blue-500)' : 'var(--border-default)',
                    background: selectedEcn?.id === ecn.id ? 'var(--blue-50)' : 'var(--bg-surface)',
                  }}
                >
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                    {ecn.code}
                  </span>
                  <span style={{ fontSize: 13 }}>{ecn.description}</span>
                  <span
                    className="kuun-caption"
                    style={{ marginLeft: 'auto', color: 'var(--fg-muted)' }}
                  >
                    적용 {ecn.appliedActiveRobotCount}대
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 선택 ECN의 영향 로봇 + 일괄 ServiceOrder. */}
      {selectedEcn && (
        <div style={card}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 10,
            }}
          >
            <strong style={{ fontSize: 13, fontFamily: 'var(--font-mono)' }}>
              {selectedEcn.code}
            </strong>
            <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
              {selectedEcn.description}
            </span>
            <label
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
              }}
            >
              <input
                type="checkbox"
                checked={operatingOnly}
                onChange={(e) => {
                  setOperatingOnly(e.target.checked);
                  setChecked(new Set());
                }}
              />
              {/* 2026-09-23 — 운영중(고객 배치)만이 아니라 '기타'(행사·파일럿·인증·테스트·
                  사내)도 포함한다. 리콜은 "현장에 나가 있나" 를 본다. */}
              현장에 나간 로봇만
            </label>
            <button
              type="button"
              style={secondaryBtn}
              onClick={exportCsv}
              disabled={robots.length === 0}
            >
              CSV
            </button>
            <button
              type="button"
              style={{ ...primaryBtn, opacity: checked.size === 0 || busy ? 0.5 : 1 }}
              disabled={checked.size === 0 || busy}
              onClick={() => recall.mutate(Array.from(checked))}
            >
              고른 로봇 {checked.size}대에 서비스 오더 만들기
            </button>
          </div>

          {robotsQ.isLoading ? (
            <div style={empty}>불러오는 중…</div>
          ) : robotsQ.error ? (
            <div style={errorBox}>{extractApiError(robotsQ.error)}</div>
          ) : robots.length === 0 ? (
            <div style={emptyBlock}>
              {operatingOnly
                ? '이 변경점이 적용된 현장 로봇이 없습니다.'
                : '이 변경점이 적용된 로봇이 없습니다.'}
            </div>
          ) : (
            <table style={tableBase}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 32 }}>
                    <input
                      type="checkbox"
                      aria-label="전체 선택"
                      checked={checked.size > 0 && checked.size === robots.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th style={th}>#시리얼</th>
                  <th style={th}>모델</th>
                  <th style={th}>고객사</th>
                  <th style={th}>매장</th>
                  <th style={{ ...th, textAlign: 'center' }}>상태</th>
                  <th style={{ ...th, textAlign: 'right' }}>적용일</th>
                  <th style={{ ...th, textAlign: 'right' }}>작업</th>
                </tr>
              </thead>
              <tbody>
                {robots.map((r) => (
                  <RecallRow
                    key={r.robotId}
                    robot={r}
                    checked={checked.has(r.robotId)}
                    onToggle={() => toggle(r.robotId)}
                    onCreateOne={() => recall.mutate([r.robotId])}
                    busy={busy}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 부품 요약 컨텍스트 (보증 임박 + 결함 LOT 영향 부품). */}
      <PartsSummaryContext />
    </div>
  );
}

function RecallRow({
  robot,
  checked,
  onToggle,
  onCreateOne,
  busy,
}: {
  robot: EcnRobotDto;
  checked: boolean;
  onToggle: () => void;
  onCreateOne: () => void;
  busy: boolean;
}) {
  const serial = robot.serialShort ?? robot.serialLong ?? robot.robotId.slice(0, 8);
  return (
    <tr style={{ borderTop: '1px solid var(--border-subtle)' }}>
      <td style={td}>
        <input
          type="checkbox"
          aria-label={`${serial} 선택`}
          checked={checked}
          onChange={onToggle}
        />
      </td>
      <td style={tdMono}>
        <Link to={`/robots/${robot.robotId}`} style={linkStyle}>
          #{serial}
        </Link>
      </td>
      <td style={td}>{robot.modelCode || '—'}</td>
      <td style={td}>
        {robot.customerDisplayName ?? <span style={{ color: 'var(--fg-muted)' }}>—</span>}
      </td>
      <td style={td}>{robot.storeName ?? <span style={{ color: 'var(--fg-muted)' }}>—</span>}</td>
      <td style={tdCenter}>{robot.status ? robotStatusLabel(robot.status) : '—'}</td>
      <td style={{ ...tdMono, textAlign: 'right', color: 'var(--fg-muted)' }}>
        {kstYmd(robot.appliedAt) ?? '—'}
      </td>
      <td style={{ ...td, textAlign: 'right' }}>
        <button type="button" style={miniBtn} disabled={busy} onClick={onCreateOne}>
          서비스 오더
        </button>
      </td>
    </tr>
  );
}

// 부품 요약 (보증 임박 / 결함 LOT 영향 부품 / 카테고리별) — 추적 보조 컨텍스트.
function PartsSummaryContext() {
  const q = useQuery({
    queryKey: ['fleet', 'parts-summary'],
    queryFn: () => dashboardApi.fleetPartsSummary(),
    staleTime: 60_000,
  });

  if (q.isLoading || q.error || !q.data) return null;
  const d = q.data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
        <h3 className="kuun-h4" style={{ margin: 0 }}>
          부품 위험 요약
        </h3>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        <RiskCard
          label="보증 30일 이내 만료"
          value={d.warrantyExpiringIn30Days}
          loading={false}
          tone={d.warrantyExpiringIn30Days > 0 ? 'warning' : 'default'}
          hint="보증 만료일 기준입니다. 미리 교체를 계획해 주세요."
        />
        <RiskCard
          label="운영 중인 결함 LOT 부품"
          value={d.defectiveLotPartsInOperation}
          loading={false}
          tone={d.defectiveLotPartsInOperation > 0 ? 'danger' : 'default'}
          hint="결함이 의심되거나 확정된 LOT의 장착 부품"
        />
      </div>
      {d.byCategory.length > 0 && (
        <table style={tableBase}>
          <thead>
            <tr>
              <th style={th}>카테고리</th>
              <th style={{ ...th, textAlign: 'right' }}>카탈로그</th>
              <th style={{ ...th, textAlign: 'right' }}>운영 중</th>
            </tr>
          </thead>
          <tbody>
            {d.byCategory.map((row) => (
              <tr key={row.category} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td style={td}>{PART_CATEGORY_LABEL[row.category]}</td>
                <td style={{ ...tdMono, textAlign: 'right' }}>{row.catalogCount}</td>
                <td
                  style={{
                    ...tdMono,
                    textAlign: 'right',
                    fontWeight: 600,
                    color: row.inUseCount > 0 ? 'var(--fg-default)' : 'var(--fg-muted)',
                  }}
                >
                  {row.inUseCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// CSV 셀 escape — 콤마/따옴표/개행 포함 시 따옴표로 감싸고 내부 따옴표는 이중화.
function csvCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// ============================================================
// §3.6 (S3 Phase 3.5) — 솔루션 추적 sub-view (매장 단위, Phase 2.8 소비)
// ============================================================
//
// 두 모드:
//   (a) 솔루션 선택 → 이 솔루션을 쓰는 매장 전체 (storesForSolution).
//   (b) 매장 클릭 → 이 매장의 솔루션 구성 (listForStore).
// 불판 · Store ID 는 고객사 마스터에서 로봇 줄마다 적는 값을, 그 매장의 계약에서
// 찾아 보여 준다(서버 solutions.service resolveContractLineFactsByStore — 어느 지점
// 계약인지 모르면 '—'). 'Store ID' 는 매장 내부 키가 아니다. 하단에 솔루션별 활성 설치
// 요약 컨텍스트 유지.

const SOLUTION_CATEGORY_LABEL: Record<SolutionCategory, string> = {
  MEAT: '육류',
  SEAFOOD: '해산물',
  VEGETABLE: '채소',
  DEMO: '데모',
  CUSTOM: '맞춤',
};

const SOLUTION_STATUS_LABEL: Record<SolutionStatus, string> = {
  BETA: '시범',
  STABLE: '정식',
  DEPRECATED: '단종 예정',
  RETIRED: '단종',
};

type SolutionViewMode = 'solution' | 'customer';

function SolutionSubView() {
  // 보기 모드: 솔루션별(역추적) ↔ 고객사별 rollup (S3 follow-up).
  const [mode, setMode] = useState<SolutionViewMode>('solution');
  // 선택된 솔루션 / 매장.
  const [solutionDefId, setSolutionDefId] = useState('');
  const [picked, setPicked] = useState<{ storeId: string; storeName: string } | null>(null);

  // 솔루션 목록 (드롭다운 + 매핑).
  const defsQ = useQuery({
    queryKey: ['fleet', 'solution-defs'],
    queryFn: () => solutionsApi.list(),
    staleTime: 60_000,
  });

  // (a) 이 솔루션을 쓰는 매장 전체.
  const storesQ = useQuery({
    queryKey: ['fleet', 'solution-stores', solutionDefId],
    queryFn: () => solutionsApi.storesForSolution(solutionDefId),
    enabled: solutionDefId.length > 0,
    staleTime: 30_000,
  });

  // (b) 이 매장의 솔루션 구성.
  const storeSolQ = useQuery({
    queryKey: ['fleet', 'store-solutions', picked?.storeId],
    queryFn: () => solutionsApi.listForStore(picked!.storeId),
    enabled: !!picked,
    staleTime: 30_000,
  });

  // (c) 고객사별 매장 솔루션 rollup (S3 follow-up).
  const byCustomerQ = useQuery({
    queryKey: ['fleet', 'solutions-by-customer'],
    queryFn: () => solutionsApi.byCustomer(),
    enabled: mode === 'customer',
    staleTime: 30_000,
  });

  const defs = defsQ.data ?? [];
  const stores = storesQ.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h3 className="kuun-h4" style={{ margin: 0 }}>
          매장별 솔루션 추적
        </h3>
        <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
          솔루션을 고르면 그 솔루션을 쓰는 매장이 모두 나오고, 매장을 누르면 그 매장의 솔루션 구성이
          보입니다. 고객사별 보기에서는 고객사마다 매장과 솔루션 구성을 한눈에 볼 수 있습니다.
        </div>
      </div>

      {/* 보기 모드 토글 — 솔루션별 ↔ 고객사별 (S3 follow-up). */}
      <div
        role="tablist"
        aria-label="솔루션 추적 보기 모드"
        style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-subtle)' }}
      >
        <SolutionModeTab active={mode === 'solution'} onClick={() => setMode('solution')}>
          솔루션별 보기
        </SolutionModeTab>
        <SolutionModeTab active={mode === 'customer'} onClick={() => setMode('customer')}>
          고객사별 보기
        </SolutionModeTab>
      </div>

      {mode === 'customer' ? (
        <CustomerSolutionRollupView
          loading={byCustomerQ.isLoading}
          error={byCustomerQ.error ? extractApiError(byCustomerQ.error) : null}
          rollup={byCustomerQ.data ?? []}
        />
      ) : (
        <>
          {/* (a) 솔루션 → 매장 전체. */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="solution-pick" style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
              솔루션
            </label>
            <select
              id="solution-pick"
              aria-label="솔루션 선택"
              value={solutionDefId}
              onChange={(e) => {
                setSolutionDefId(e.target.value);
                setPicked(null);
              }}
              style={{
                height: 34,
                padding: '0 10px',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-input)',
                fontSize: 13,
                minWidth: 240,
              }}
            >
              <option value="">— 솔루션 선택 —</option>
              {defs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName} ({s.code})
                </option>
              ))}
            </select>
          </div>

          {solutionDefId && (
            <div style={card}>
              <strong style={{ fontSize: 13 }}>이 솔루션을 쓰는 매장</strong>
              {storesQ.isLoading ? (
                <div style={empty}>불러오는 중…</div>
              ) : storesQ.error ? (
                <div style={errorBox}>{extractApiError(storesQ.error)}</div>
              ) : stores.length === 0 ? (
                <div style={{ ...emptyBlock, marginTop: 8 }}>이 솔루션을 쓰는 매장이 없습니다.</div>
              ) : (
                <table style={tableBase}>
                  <thead>
                    <tr>
                      <th style={th}>매장</th>
                      <th style={th}>고객사</th>
                      <th style={th}>불판</th>
                      <th style={th}>Store ID</th>
                      <th style={{ ...th, textAlign: 'right' }}>활성화일</th>
                      <th style={{ ...th, textAlign: 'center' }}>상태</th>
                      <th style={{ ...th, textAlign: 'right' }}>구성</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stores.map((s) => (
                      <SolutionStoreRow
                        key={s.storeSolutionId}
                        usage={s}
                        onPick={() =>
                          setPicked({
                            storeId: s.storeId,
                            storeName: [s.storeName, s.branchName].filter(Boolean).join(' · '),
                          })
                        }
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* (b) 매장 → 솔루션 구성. */}
          {picked && (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <strong style={{ fontSize: 13 }}>{picked.storeName} 솔루션 구성</strong>
                <button
                  type="button"
                  style={{ ...miniBtn, marginLeft: 'auto' }}
                  onClick={() => setPicked(null)}
                >
                  닫기
                </button>
              </div>
              {/* 매장 메타: 고객사 + 불판 (S3 follow-up). */}
              {(storeSolQ.data ?? []).length > 0 && (
                <div
                  className="kuun-caption"
                  style={{ display: 'flex', gap: 16, marginBottom: 8, color: 'var(--fg-muted)' }}
                >
                  <span>
                    고객사:{' '}
                    <strong style={{ color: 'var(--fg-default)' }}>
                      {storeSolQ.data?.[0]?.storeCustomerName ?? '—'}
                    </strong>
                  </span>
                  <span>
                    불판:{' '}
                    <strong style={{ color: 'var(--fg-default)' }}>
                      {storeSolQ.data?.[0]?.storeGrillPlate ?? '—'}
                    </strong>
                  </span>
                </div>
              )}
              {storeSolQ.isLoading ? (
                <div style={empty}>불러오는 중…</div>
              ) : storeSolQ.error ? (
                <div style={errorBox}>{extractApiError(storeSolQ.error)}</div>
              ) : (storeSolQ.data ?? []).length === 0 ? (
                <div style={emptyBlock}>이 매장에 활성 솔루션이 없습니다.</div>
              ) : (
                <table style={tableBase}>
                  <thead>
                    <tr>
                      <th style={th}>솔루션</th>
                      <th style={th}>카테고리</th>
                      <th style={th}>상태</th>
                      <th style={{ ...th, textAlign: 'right' }}>활성화일</th>
                      <th style={{ ...th, textAlign: 'right' }}>가동률</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(storeSolQ.data ?? []).map((ss) => (
                      <tr key={ss.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{ss.solutionDef.displayName}</div>
                          <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
                            {ss.solutionDef.code}
                          </div>
                        </td>
                        <td style={td}>{SOLUTION_CATEGORY_LABEL[ss.solutionDef.category]}</td>
                        <td style={td}>
                          {ss.deactivatedAt
                            ? '비활성'
                            : SOLUTION_STATUS_LABEL[ss.solutionDef.status]}
                        </td>
                        <td style={{ ...tdMono, textAlign: 'right', color: 'var(--fg-muted)' }}>
                          {kstYmd(ss.activatedAt) ?? '—'}
                        </td>
                        <td style={{ ...tdMono, textAlign: 'right' }}>
                          {ss.uptimePercent != null ? `${ss.uptimePercent}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* 솔루션별 활성 설치 요약 컨텍스트. */}
          <SolutionsSummaryContext />
        </>
      )}
    </div>
  );
}

// 보기 모드 토글 버튼 (솔루션별 / 고객사별).
function SolutionModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: '8px 14px',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--blue-600)' : '2px solid transparent',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--fg-default)' : 'var(--fg-muted)',
      }}
    >
      {children}
    </button>
  );
}

function SolutionStoreRow({ usage, onPick }: { usage: SolutionStoreUsageDto; onPick: () => void }) {
  const active = usage.deactivatedAt == null;
  return (
    <tr style={{ borderTop: '1px solid var(--border-subtle)' }}>
      <td style={td}>{[usage.storeName, usage.branchName].filter(Boolean).join(' · ')}</td>
      <td style={td}>{usage.customerName ?? '—'}</td>
      <td style={td}>{usage.grillPlate ?? '—'}</td>
      {/* 'Store ID' = 고객사 마스터 '#n 로봇 Store ID' 에 사람이 적은 값(2026-09-30).
          종전엔 매장 내부 키 앞 12자를 찍어 사람이 적은 Store ID 로 오해하게 했다.
          `?? []` — 새 콘솔이 옛 서버(필드 없음)를 만나도 표가 깨지지 않고 '—'. */}
      <td style={td}>{(usage.contractStoreIds ?? []).join(', ') || '—'}</td>
      <td style={{ ...tdMono, textAlign: 'right', color: 'var(--fg-muted)' }}>
        {kstYmd(usage.activatedAt) ?? '—'}
      </td>
      <td style={tdCenter}>
        {active ? '활성' : <span style={{ color: 'var(--fg-muted)' }}>비활성</span>}
      </td>
      <td style={{ ...td, textAlign: 'right' }}>
        <button type="button" style={miniBtn} onClick={onPick}>
          솔루션 구성
        </button>
      </td>
    </tr>
  );
}

// 고객사별 매장 솔루션 rollup 뷰 (S3 follow-up, spec §3.6).
function CustomerSolutionRollupView({
  loading,
  error,
  rollup,
}: {
  loading: boolean;
  error: string | null;
  rollup: CustomerSolutionRollupDto[];
}) {
  if (loading) return <div style={empty}>불러오는 중…</div>;
  if (error) return <div style={errorBox}>{error}</div>;
  if (rollup.length === 0)
    return <div style={emptyBlock}>활성 솔루션이 있는 고객사가 없습니다.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {rollup.map((c) => (
        <div key={c.customerId || c.customerName} style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <strong style={{ fontSize: 14 }}>{c.customerName}</strong>
            <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
              매장 {c.stores.length}곳, 활성 솔루션 {c.activeSolutionCount}개
            </span>
          </div>
          <table style={tableBase}>
            <thead>
              <tr>
                <th style={th}>매장</th>
                <th style={th}>불판</th>
                <th style={th}>솔루션 구성</th>
              </tr>
            </thead>
            <tbody>
              {c.stores.map((s) => (
                <tr key={s.storeId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={td}>
                    <Link to={`/stores/${s.storeId}`} style={{ color: 'var(--blue-600)' }}>
                      {[s.storeName, s.branchName].filter(Boolean).join(' · ')}
                    </Link>
                  </td>
                  <td style={td}>{s.grillPlate ?? '—'}</td>
                  <td style={td}>
                    {s.solutions.length === 0 ? (
                      <span style={{ color: 'var(--fg-muted)' }}>—</span>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {s.solutions.map((sol) => (
                          <span
                            key={sol.storeSolutionId}
                            title={`${sol.code} · ${SOLUTION_CATEGORY_LABEL[sol.category]}`}
                            style={{
                              fontSize: 12,
                              padding: '2px 8px',
                              borderRadius: 'var(--r-pill)',
                              background: 'var(--bg-surface-raised)',
                              border: '1px solid var(--border-default)',
                            }}
                          >
                            {sol.displayName}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// 솔루션별 활성 설치 + 평균 가동률 요약 — 추적 보조 컨텍스트.
function SolutionsSummaryContext() {
  const q = useQuery({
    queryKey: ['fleet', 'solutions-summary'],
    queryFn: () => dashboardApi.fleetSolutionsSummary(),
    staleTime: 60_000,
  });

  if (q.isLoading || q.error || !q.data || q.data.byDef.length === 0) return null;
  const d = q.data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
        <h3 className="kuun-h4" style={{ margin: 0 }}>
          솔루션별 활성 설치와 평균 가동률
        </h3>
      </div>
      <table style={tableBase}>
        <thead>
          <tr>
            <th style={th}>솔루션</th>
            <th style={th}>카테고리</th>
            <th style={th}>상태</th>
            <th style={{ ...th, textAlign: 'right' }}>활성 설치</th>
            <th style={{ ...th, textAlign: 'right' }}>평균 가동률</th>
          </tr>
        </thead>
        <tbody>
          {d.byDef.map((row) => (
            <tr key={row.solutionDefId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>{row.displayName}</div>
              </td>
              <td style={td}>{SOLUTION_CATEGORY_LABEL[row.category]}</td>
              <td style={td}>{SOLUTION_STATUS_LABEL[row.status]}</td>
              <td style={{ ...tdMono, textAlign: 'right', fontWeight: 600 }}>
                {row.activeInstalls}
              </td>
              <td style={{ ...tdMono, textAlign: 'right' }}>
                {row.avgUptimePercent != null ? `${row.avgUptimePercent}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const emptyBlock: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  border: '1px dashed var(--border-default)',
  borderRadius: 'var(--r-card)',
  fontSize: 13,
};

// ============================================================
// Risk indicator card
// ============================================================

interface RiskCardProps {
  label: string;
  value: number | null;
  loading: boolean;
  tone?: 'default' | 'warning' | 'danger';
  hint?: string;
  disabled?: boolean;
}

function RiskCard({ label, value, loading, tone = 'default', hint, disabled }: RiskCardProps) {
  const bg = disabled
    ? 'var(--bg-surface)'
    : tone === 'danger'
      ? 'var(--bg-danger-subtle)'
      : tone === 'warning'
        ? 'var(--bg-warning-subtle)'
        : 'var(--bg-surface)';
  return (
    <div
      style={{
        ...card,
        padding: 14,
        opacity: disabled ? 0.55 : 1,
        background: bg,
      }}
    >
      <div className="kuun-label" style={{ color: 'var(--fg-muted)' }}>
        {label}
      </div>
      <div
        className="kuun-kpi"
        style={{
          marginTop: 4,
          color: tone === 'danger' && !disabled ? 'var(--fg-danger)' : undefined,
        }}
      >
        {disabled ? '—' : loading ? '…' : value === null ? '—' : value.toLocaleString('ko-KR')}
      </div>
      {hint && (
        <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Tab button
// ============================================================

function TabButton({
  active,
  onClick,
  children,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '10px 16px',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--blue-600)' : '2px solid transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: disabled ? 'var(--fg-subtle)' : active ? 'var(--fg-default)' : 'var(--fg-muted)',
      }}
    >
      {children}
    </button>
  );
}

// ============================================================
// Styles
// ============================================================

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 16,
};

const tabStrip: CSSProperties = {
  display: 'flex',
  gap: 4,
  borderBottom: '1px solid var(--border-subtle)',
  padding: '0 12px',
};

const trackBlock: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  padding: 12,
  background: 'var(--bg-surface)',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const trackHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 6,
};

const versionRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 0',
};

const tableBase: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: 8,
};

const th: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  padding: '8px 10px',
  borderBottom: '1px solid var(--border-subtle)',
};

const td: CSSProperties = {
  fontSize: 13,
  padding: '8px 10px',
};

const tdCenter: CSSProperties = { ...td, textAlign: 'center' };
const tdMono: CSSProperties = { ...td, fontFamily: 'var(--font-mono)', fontSize: 12 };

const empty: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  fontSize: 13,
  color: 'var(--fg-muted)',
};

const errorBox: CSSProperties = {
  padding: 10,
  marginTop: 8,
  background: 'var(--bg-danger-subtle)',
  border: '1px solid var(--fg-danger)',
  borderRadius: 'var(--r-card)',
  color: 'var(--fg-danger)',
  fontSize: 12,
};

const linkStyle: CSSProperties = {
  color: 'var(--fg-brand)',
  textDecoration: 'none',
};

// §3.6 부품/솔루션 추적 액션 버튼 (인라인 스타일 — 이 페이지 패턴 유지).
const primaryBtn: CSSProperties = {
  height: 34,
  padding: '0 14px',
  border: 'none',
  borderRadius: 'var(--r-btn)',
  background: 'var(--blue-600)',
  color: 'var(--fg-inverse)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryBtn: CSSProperties = {
  height: 34,
  padding: '0 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  cursor: 'pointer',
};

const miniBtn: CSSProperties = {
  height: 26,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 12,
  cursor: 'pointer',
};

const ecnRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  textAlign: 'left',
  padding: '8px 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  cursor: 'pointer',
};
